import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { ApiError, hasLiveApi } from '@/api/client'
import { keys } from '@/api/hooks/shared/keys'
import {
  deleteBlockedDeviceId,
  getUserLog,
  saveBlockedDeviceId,
  toLoginHistoryResult,
  type LoginHistoryResult,
} from '@/api/map/userLog'

export type LoginHistoryQuery = {
  memberId: string
  fromDate: string
  toDate: string
  page?: number
  pageSize?: number
  /** Bumped on every Search so same dates still hit the network. */
  searchNonce?: number
}

function emptyResult(query: LoginHistoryQuery): LoginHistoryResult {
  const pageSize = Math.max(1, query.pageSize ?? DEFAULT_PAGE_SIZE)
  const page = Math.max(1, query.page ?? 1)
  return { rows: [], total: 0, page, pageSize }
}

/** Exported for Vitest whitebox (soft-empty / throw / enabled gate). */
export async function loadLoginHistory(query: LoginHistoryQuery): Promise<LoginHistoryResult> {
  try {
    const live = await getUserLog({
      memberId: query.memberId,
      fromDate: query.fromDate,
      toDate: query.toDate,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? DEFAULT_PAGE_SIZE,
    })
    if (!live.value) return emptyResult(query)
    return toLoginHistoryResult(live, query)
  } catch (err) {
    const status = err instanceof ApiError ? err.status : 0
    // Soft-empty only when the route is missing / down — other faults must surface.
    if (status === 404 || status === 503) {
      console.warn('[login-history] hierarchy getuserLog unavailable', err)
      return emptyResult(query)
    }
    throw err
  }
}

/** Member Detail → Login History — Agent-API `getuserLog`. */
export function useLoginHistory(query: LoginHistoryQuery, enabled = true) {
  const { searchNonce: _searchNonce, ...request } = query
  return useQuery({
    queryKey: keys.loginHistory(query),
    queryFn: () => loadLoginHistory(request),
    // hasLiveApi in enabled (not queryFn) so we never "succeed" with a silent empty skip.
    enabled: enabled && Boolean(query.memberId) && hasLiveApi(),
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: (previous) => previous,
  })
}

export function useSaveMemberBlockedDeviceId() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { memberId: string; comment?: string }) => {
      if (!hasLiveApi()) {
        throw new ApiError(503, 'Hierarchy API is not configured', null)
      }
      const res = await saveBlockedDeviceId(input)
      if (!res.value) {
        throw new ApiError(409, res.error || 'Unable to block device id', res)
      }
      return res
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['login-history'] })
      void client.invalidateQueries({ queryKey: ['blocked-devices'] })
    },
  })
}

export function useDeleteMemberBlockedDeviceId() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { memberId: string }) => {
      if (!hasLiveApi()) {
        throw new ApiError(503, 'Hierarchy API is not configured', null)
      }
      const res = await deleteBlockedDeviceId(input)
      if (!res.value) {
        throw new ApiError(409, res.error || 'Unable to remove blocked device id', res)
      }
      return res
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['login-history'] })
      void client.invalidateQueries({ queryKey: ['blocked-devices'] })
    },
  })
}
